import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Container, Row, Col, Card, CardHeader, CardBody, Nav, NavItem, NavLink, Badge as BadgeUI, Button, ButtonGroup } from "reactstrap";
import NotificationAlert from "react-notification-alert";
import classnames from "classnames";
import api from "./../../../Api.js";
import SimpleHeader from "./../../../components/Headers/SimpleHeader.jsx";
import PixelCharacterFigure from "./PixelCharacterFigure.jsx";
import PixelInventoryItem from "./PixelInventoryItem.jsx";
import HpBar from "./../rpg/HpBar.jsx";
import GoldDisplay from "./../rpg/GoldDisplay.jsx";
import XpBar from "./XpBar.jsx";
import { DIRECTIONS } from "./../../../helper/lpcHelper.js";

/**
 * CharacterRpg — pagina do personagem RPG pixel art.
 * - Catalogo completo (items bloqueados com cadeado)
 * - Rotacao 360 (4 direcoes)
 * - Admin recebe tudo desbloqueado automaticamente
 */
class CharacterRpg extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      catalog: [],
      status: null,
      activeTab: 'all',
      direction: DIRECTIONS.SOUTH, // de frente por padrao (olhando pro aluno)
      animation: 'idle',           // respirando; muda ao clicar nas acoes
      loading: true
    };
  }

  async componentDidMount() {
    await Promise.all([this.loadCatalog(), this.loadStatus()]);
  }

  async loadStatus() {
    if (!this.props.user || !this.props.user.id) return;
    try {
      var res = await api.getRpgStatus(this.props.user.id);
      this.setState({ status: res.data || null });
    } catch (err) {
      this.notify('danger', 'Ops!', 'Não consegui carregar o status do herói 😢');
    }
  }

  async loadCatalog() {
    if (!this.props.user || !this.props.user.id) return;
    try {
      // Inicializa items default/admin (idempotente)
      try { await api.initCosmetics(this.props.user.id); } catch (e) {}

      // Carrega catalogo completo (com flag owned, inclui itens gerados por IA do aluno)
      var res = await api.getCosmeticCatalogUser(this.props.user.id);
      var data = res.data;
      this.setState({ catalog: Array.isArray(data) ? data : [], loading: false });
    } catch (err) {
      this.setState({ loading: false });
      this.notify('danger', 'Ops!', 'Não consegui carregar o catálogo de itens 😢');
    }
  }

  notify(type, title, message) {
    try {
      this.refs.notificationAlert.notificationAlert({
        place: 'tr',
        message: (
          <div className="alert-text">
            <span className="alert-title">{title}</span>
            <span> {message}</span>
          </div>
        ),
        type: type,
        icon: type === 'success' ? 'ni ni-check-bold' : 'ni ni-bell-55',
        autoDismiss: 4
      });
    } catch (e) {}
  }

  async toggleItem(item) {
    if (!this.props.user || !this.props.user.id) return;
    if (!item.owned) {
      this.notify('warning', 'Item bloqueado', 'Conquiste o requisito pra desbloquear');
      return;
    }
    try {
      if (item.equipped) {
        var res = await api.unequipCosmetic({ user: this.props.user.id, item: item.id });
        var out = res.data || {};
        if (out.kept) {
          //peca basica: o heroi nao pode ficar sem ela
          this.notify('warning', '🙈 Peça básica', out.message || 'Seu herói não pode ficar sem essa peça!');
        } else if (out.replacedBy) {
          this.notify('info', 'Voltou pro básico', out.replacedBy.name);
        } else {
          this.notify('info', 'Removido', item.name);
        }
      } else {
        await api.equipCosmetic({ user: this.props.user.id, item: item.id });
        this.notify('success', 'Equipado!', item.name);
      }
      await this.loadCatalog();
    } catch (e) {
      this.notify('warning', 'Erro', 'Falha ao equipar');
    }
  }

  // Filtra items LPC do catalog
  getLpcItems() {
    return this.state.catalog.filter(function (i) { return !!i.lpcLayer; });
  }

  // Items equipados (no formato esperado por PixelCharacterFigure)
  getEquipped() {
    return this.getLpcItems().filter(function (i) { return i.equipped; });
  }

  getBySlot(slot) {
    if (slot === 'all') return this.getLpcItems();
    return this.getLpcItems().filter(function (i) { return i.slot === slot; });
  }

  countBySlot(slot) {
    var items = this.getBySlot(slot);
    var owned = items.filter(function (i) { return i.owned; }).length;
    return { total: items.length, owned: owned };
  }

  setDirection(dir) {
    this.setState({ direction: dir });
  }

  render() {
    var equipped = this.getEquipped();
    var slots = [
      { key: 'all', label: 'Todos', icon: 'fas fa-th' },
      { key: 'body', label: 'Corpo', icon: 'fas fa-male' },
      { key: 'head', label: 'Cabeça', icon: 'fas fa-user-circle' },
      { key: 'eyes', label: 'Olhos', icon: 'fas fa-eye' },
      { key: 'beard', label: 'Barba', icon: 'fas fa-user-tie' },
      { key: 'mustache', label: 'Bigode', icon: 'fas fa-user' },
      { key: 'glasses', label: 'Óculos', icon: 'fas fa-glasses' },
      { key: 'top', label: 'Cabelo', icon: 'fas fa-hat-wizard' },
      { key: 'helmet', label: 'Capacete', icon: 'fas fa-hard-hat' },
      { key: 'torso', label: 'Armadura', icon: 'fas fa-shield-alt' },
      { key: 'legs', label: 'Calça', icon: 'fas fa-running' },
      { key: 'feet', label: 'Botas', icon: 'fas fa-shoe-prints' },
      { key: 'weapon', label: 'Arma', icon: 'fas fa-khanda' },
      { key: 'shield', label: 'Escudo', icon: 'fas fa-shield' }
    ];

    var allLpc = this.getLpcItems();
    var totalOwned = allLpc.filter(function (i) { return i.owned; }).length;

    return (
      <React.Fragment>
        <div className="rna-wrapper"><NotificationAlert ref="notificationAlert" /></div>
        {!this.props.embedded && <SimpleHeader name="Meu Personagem" way={[{ name: "Conquistas", url: "/character-rpg" }]} />}

        <Container className={this.props.embedded ? "mt-3" : "mt--6"} fluid>

          {/* Status vital: HP, Level/XP e Gold (no hub /game a faixa do jogador ja mostra isso) */}
          {!this.props.embedded &&
          <Row>
            <Col xs="12">
              <Card className="shadow mb-4">
                <CardBody className="py-3">
                  <Row className="align-items-center">
                    <Col md="4" className="mb-3 mb-md-0">
                      {this.state.status && this.state.status.hp ? (
                        <HpBar
                          currentHp={this.state.status.hp.currentHp}
                          maxHp={this.state.status.hp.maxHp}
                        />
                      ) : (
                        <small className="text-muted">
                          <i className="fas fa-heart mr-1" />Carregando vida...
                        </small>
                      )}
                    </Col>
                    <Col md="4" className="mb-3 mb-md-0">
                      {this.state.status && this.state.status.xp ? (
                        <XpBar
                          totalXp={this.state.status.xp.totalXp}
                          currentLevel={this.state.status.xp.currentLevel}
                          nextLevel={this.state.status.xp.nextLevel}
                          xpToNextLevel={this.state.status.xp.xpToNextLevel}
                          percentToNext={this.state.status.xp.percentToNext}
                          compact
                        />
                      ) : (
                        <small className="text-muted">
                          <i className="fas fa-star mr-1" />Carregando XP...
                        </small>
                      )}
                    </Col>
                    <Col md="4" className="text-center text-md-right">
                      <GoldDisplay
                        balance={this.state.status && this.state.status.gold ? this.state.status.gold.balance : 0}
                      />
                    </Col>
                  </Row>
                  <hr className="my-3" />
                  <div className="text-center">
                    <Link to="/game/estilista" className="btn btn-sm btn-primary mb-1 mr-2">
                      ✨ Estilista IA
                    </Link>
                    <Link to="/game/diario" className="btn btn-sm btn-info mb-1 mr-2">
                      📜 Diário do Herói
                    </Link>
                    <Link to="/game/lojinha" className="btn btn-sm btn-warning mb-1">
                      🪙 Lojinha
                    </Link>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          }

          <Row>

            {/* Personagem grande */}
            <Col xl="4" lg="5">
              <Card className="shadow text-center">
                <CardHeader className="border-0 text-center">
                  <h3 className="mb-0" style={{ fontFamily: 'monospace', letterSpacing: '2px' }}>
                    <i className="fas fa-dragon mr-2" style={{ color: '#FF5722' }} />
                    HERÓI
                  </h3>
                </CardHeader>
                <CardBody style={{ padding: '20px' }}>
                  <PixelCharacterFigure
                    equipped={equipped}
                    scale={5}
                    direction={this.state.direction}
                    animation={this.state.animation}
                    onAnimationEnd={() => this.setState({ animation: 'idle' })}
                  />

                  {/* Ver o heroi em acao */}
                  <div className="mt-3">
                    <ButtonGroup size="sm">
                      <Button color="primary" onClick={() => this.setState({ animation: 'slash' })} title="Atacar">
                        ⚔️ Atacar
                      </Button>
                      <Button color="info" onClick={() => this.setState({ animation: 'walk' })} title="Andar">
                        🚶 Andar
                      </Button>
                      <Button color="success" onClick={() => this.setState({ animation: 'spellcast' })} title="Magia">
                        ✨ Magia
                      </Button>
                      <Button color="secondary" onClick={() => this.setState({ animation: 'idle' })} title="Parar">
                        😌 Parar
                      </Button>
                    </ButtonGroup>
                  </div>

                  {/* Botoes de rotacao 360 */}
                  <ButtonGroup size="sm" className="mt-3">
                    <Button
                      color={this.state.direction === DIRECTIONS.NORTH ? "warning" : "secondary"}
                      onClick={() => this.setDirection(DIRECTIONS.NORTH)}
                      title="Norte (costas)"
                    >
                      <i className="fas fa-arrow-up" />
                    </Button>
                    <Button
                      color={this.state.direction === DIRECTIONS.WEST ? "warning" : "secondary"}
                      onClick={() => this.setDirection(DIRECTIONS.WEST)}
                      title="Oeste"
                    >
                      <i className="fas fa-arrow-left" />
                    </Button>
                    <Button
                      color={this.state.direction === DIRECTIONS.SOUTH ? "warning" : "secondary"}
                      onClick={() => this.setDirection(DIRECTIONS.SOUTH)}
                      title="Sul (frente)"
                    >
                      <i className="fas fa-arrow-down" />
                    </Button>
                    <Button
                      color={this.state.direction === DIRECTIONS.EAST ? "warning" : "secondary"}
                      onClick={() => this.setDirection(DIRECTIONS.EAST)}
                      title="Leste"
                    >
                      <i className="fas fa-arrow-right" />
                    </Button>
                  </ButtonGroup>
                  <div className="mt-1">
                    <small className="text-muted" style={{ fontSize: '0.65rem' }}>360° view</small>
                  </div>

                  <h4 className="mt-3 mb-1">{this.props.user ? this.props.user.name : 'Aventureiro'}</h4>
                  <small className="text-muted">
                    <i className="fas fa-shopping-bag mr-1" />
                    {totalOwned}/{allLpc.length} itens · {equipped.length} equipados
                  </small>

                  {/* Atributos vindos do equipamento */}
                  {this.state.status && this.state.status.equipment &&
                   (this.state.status.equipment.bonusHp > 0 || this.state.status.equipment.bonusDamage > 0) && (
                    <small className="d-block mt-1" style={{ fontWeight: 700 }}>
                      {this.state.status.equipment.bonusHp > 0 && (
                        <span className="text-success mr-2" title="Vida extra do equipamento">
                          <i className="fas fa-heart mr-1" />+{this.state.status.equipment.bonusHp} HP
                        </span>
                      )}
                      {this.state.status.equipment.bonusDamage > 0 && (
                        <span className="text-danger" title="Dano extra do equipamento">
                          <i className="fas fa-fist-raised mr-1" />+{this.state.status.equipment.bonusDamage} dano
                        </span>
                      )}
                    </small>
                  )}

                  {/* Slots equipados */}
                  <div className="mt-3 text-left" style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
                    {slots.filter(function (s) { return s.key !== 'all'; }).map((s) => {
                      var slotEq = equipped.find(function (e) { return e.slot === s.key; });
                      return (
                        <div key={s.key} className="d-flex align-items-center justify-content-between mb-1" style={{ fontSize: '0.85rem' }}>
                          <span>
                            <i className={s.icon + ' mr-2'} style={{ width: '16px', color: slotEq ? '#FFD700' : '#ccc' }} />
                            <strong>{s.label}:</strong>
                          </span>
                          <span style={{ color: slotEq ? '#333' : '#ccc' }}>
                            {slotEq ? slotEq.name : <small><em>vazio</em></small>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Inventário RPG */}
            <Col xl="8" lg="7">
              <Card className="shadow">
                <CardHeader className="border-0">
                  <h3 className="mb-2">
                    <i className="fas fa-treasure-chest text-warning mr-2" />
                    Catálogo RPG
                  </h3>
                  <small className="text-muted d-block mb-3">
                    Universal LPC Spritesheet (CC-BY-SA 3.0) · Itens bloqueados aparecem com cadeado
                  </small>
                  <Nav tabs style={{ borderBottom: 'none', flexWrap: 'wrap' }}>
                    {slots.map((s) => {
                      var c = this.countBySlot(s.key);
                      return (
                        <NavItem key={s.key}>
                          <NavLink
                            className={classnames({ active: this.state.activeTab === s.key })}
                            onClick={() => this.setState({ activeTab: s.key })}
                            style={{ cursor: 'pointer', fontSize: '0.85rem', padding: '6px 12px' }}
                          >
                            <i className={s.icon + ' mr-1'} />
                            {s.label}
                            <BadgeUI color="secondary" pill className="ml-1" style={{ fontSize: '0.55rem' }}>
                              {c.owned}/{c.total}
                            </BadgeUI>
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>
                </CardHeader>
                <CardBody>
                  {this.state.loading ? (
                    <div className="text-center py-5">
                      <i className="fas fa-spinner fa-spin fa-3x text-muted" />
                    </div>
                  ) : this.getBySlot(this.state.activeTab).length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="fas fa-inbox fa-3x mb-2" style={{ opacity: 0.3 }} />
                      <p>Nenhum item nesta categoria.</p>
                    </div>
                  ) : (
                    <Row>
                      {this.getBySlot(this.state.activeTab).map((item) => (
                        <Col xs="6" sm="4" md="3" key={item.id} className="mb-3">
                          <PixelInventoryItem
                            item={Object.assign({}, item, { item: item.id })}
                            aiBadge={!!item.aiPrompt}
                            onClick={(i) => this.toggleItem(i)}
                          />
                        </Col>
                      ))}
                    </Row>
                  )}
                </CardBody>
              </Card>

              {/* Hint */}
              <Card className="shadow mt-3">
                <CardBody className="text-center py-3">
                  <i className="fas fa-scroll mr-2 text-warning" />
                  <small className="text-muted">
                    <strong>Lenda:</strong> Cada nível desbloqueia novas roupas · Conclusão de cursos forja armaduras · Streaks longos garantem itens lendários · Admins têm acesso completo
                  </small>
                </CardBody>
              </Card>
            </Col>

          </Row>
        </Container>
      </React.Fragment>
    );
  }
}

export default connect(state => ({
  user: state.user,
  translate: state.translate
}))(CharacterRpg);
